import { Capacitor } from "@capacitor/core";
import { SiApple } from "@icons-pack/react-simple-icons";
import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";
import { authErrorMessage } from "@/auth/auth-error";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";
import { desktopBridge } from "@/lib/desktop";
import { authorizeWithAppleSheet } from "./apple-native-sheet";
import { authFeatures } from "./auth-features";
import { redirectSocialSignIn } from "./oauth-resume";

interface AppleSignInButtonProps {
  onError?: (error: string) => void;
}

/** Apple can put its private-relay address in a name field; that is no name. */
function usableNamePart(part: string | null | undefined) {
  const trimmed = part?.trim();
  return trimmed && !trimmed.includes("@") ? trimmed : undefined;
}

// The name for the new account, which Apple's identity token lacks. The sheet
// gives it once per Apple ID, spent even when the sign-in after it fails, so it
// is kept for that Apple ID's retry until a sign-in succeeds.
let pendingAppleName:
  | { appleUserId: string; name: { firstName?: string; lastName?: string } }
  | undefined;

export function AppleSignInButton({ onError }: AppleSignInButtonProps) {
  const t = useTranslations("auth.appleSignInButton");
  const [loading, setLoading] = useState(false);
  // Hidden unless the build says Apple sign-in is configured, so environments
  // without Apple credentials never show a dead button.
  const { appleSignInEnabled } = authFeatures;

  // Redirect sign-in: the web, Android and the desktop shell.
  const handleRedirectAppleSignIn = useCallback(async () => {
    setLoading(true);
    const error = await redirectSocialSignIn("apple");
    // On success the page navigates to Apple; loading intentionally stays on
    // until the navigation takes over.
    if (error) {
      setLoading(false);
      onError?.(error);
    }
  }, [onError]);

  // Native iOS Apple Sign-In. The sheet itself lives in `apple-native-sheet`,
  // shared with account linking; only the better-auth call below differs.
  const handleNativeAppleSignIn = useCallback(async () => {
    setLoading(true);
    try {
      const sheet = await authorizeWithAppleSheet(t("errorFallback"));
      if (sheet.status === "canceled") {
        return;
      }
      if (sheet.status === "error") {
        onError?.(sheet.message);
        return;
      }

      const { appleUserId } = sheet;
      const firstName = usableNamePart(sheet.givenName);
      const lastName = usableNamePart(sheet.familyName);
      if (firstName || lastName) {
        pendingAppleName = { appleUserId, name: { firstName, lastName } };
      }
      const user =
        pendingAppleName?.appleUserId === appleUserId
          ? { name: pendingAppleName.name }
          : undefined;

      const { error: signInError } = await authClient.signIn.social({
        idToken: { nonce: sheet.nonce, token: sheet.token, user },
        provider: "apple",
      });

      if (signInError) {
        onError?.(authErrorMessage(signInError));
        return;
      }
      pendingAppleName = undefined;
    } catch (err: unknown) {
      onError?.(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [onError, t]);

  const handleClick = useCallback(async () => {
    if (!desktopBridge() && Capacitor.getPlatform() === "ios") {
      await handleNativeAppleSignIn();
    } else {
      await handleRedirectAppleSignIn();
    }
  }, [handleNativeAppleSignIn, handleRedirectAppleSignIn]);

  if (!appleSignInEnabled) {
    return null;
  }

  return (
    <Button
      className="w-full"
      disabled={loading}
      onClick={handleClick}
      type="button"
      variant="outline"
    >
      <SiApple className="size-4" />
      {loading ? t("signingIn") : t("continue")}
    </Button>
  );
}
